<template>
  <Teleport to="body">
    <Transition name="backdrop">
      <div
        v-if="visible"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4"
        :class="overlayClass"
        @click.self="handleBackdropClick"
      >
        <Transition name="modal" appear>
          <div
            v-if="visible"
            class="w-full max-w-sm rounded-2xl bg-white dark:bg-dark-background-modal p-6 shadow-xl"
          >
            <!-- Título con ícono inline pequeño -->
            <div class="flex items-start gap-2.5 mb-2">
              <div :class="iconBadgeClasses" class="shrink-0 mt-0.5 w-6 h-6 rounded-md flex items-center justify-center">
                <component :is="resolvedIcon" class="w-3.5 h-3.5" />
              </div>
              <h3 class="text-base font-bold text-light-text-primary dark:text-dark-text-primary leading-snug">
                {{ title }}
              </h3>
            </div>

            <p
              v-if="message"
              class="text-sm text-light-text-secondary dark:text-dark-text-secondary leading-relaxed pl-[34px]"
              :class="secondaryMessage ? 'mb-1' : 'mb-5'"
            >
              {{ message }}
            </p>
            <p
              v-if="secondaryMessage"
              class="text-xs text-light-text-secondary dark:text-dark-text-secondary mb-5 pl-[34px]"
            >
              {{ secondaryMessage }}
            </p>

            <slot name="actions">
              <div class="flex justify-end">
                <button
                  type="button"
                  :class="buttonClasses"
                  class="px-5 py-2.5 rounded-xl text-sm font-semibold transition hover:opacity-90"
                  @click="handlePrimary"
                >
                  {{ primaryLabel }}
                </button>
              </div>
            </slot>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/vue/24/outline'

const props = defineProps({
  visible: { type: Boolean, default: false },
  variant: {
    type: String,
    default: 'info',
    validator: (v) => ['success', 'error', 'warning', 'info', 'accent'].includes(v)
  },
  icon: { type: [Object, Function], default: null },
  title: { type: String, required: true },
  message: { type: String, default: '' },
  secondaryMessage: { type: String, default: '' },
  primaryLabel: { type: String, default: 'Aceptar' },
  dismissOnBackdrop: { type: Boolean, default: true },
  overlayClass: { type: String, default: '' }
})

const emit = defineEmits(['close', 'primary'])

const VARIANT_STYLES = {
  success: {
    badge: 'bg-green-100 dark:bg-green-900/40 text-green-600 dark:text-green-400',
    button: 'bg-light-accent dark:bg-dark-accent text-white',
    defaultIcon: CheckCircleIcon
  },
  error: {
    badge: 'bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400',
    button: 'bg-red-500 dark:bg-red-600 text-white',
    defaultIcon: ExclamationTriangleIcon
  },
  warning: {
    badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-500 dark:text-amber-400',
    button: 'bg-light-accent dark:bg-dark-accent text-white',
    defaultIcon: ExclamationTriangleIcon
  },
  info: {
    badge: 'bg-light-accent/10 dark:bg-dark-accent/20 text-light-accent dark:text-dark-accent',
    button: 'bg-light-accent dark:bg-dark-accent text-white',
    defaultIcon: InformationCircleIcon
  },
  accent: {
    badge: 'bg-light-accent/10 text-light-accent',
    button: 'bg-light-accent dark:bg-dark-accent text-white',
    defaultIcon: InformationCircleIcon
  }
}

const variantStyle = computed(() => VARIANT_STYLES[props.variant] || VARIANT_STYLES.info)
const resolvedIcon = computed(() => props.icon || variantStyle.value.defaultIcon)
const iconBadgeClasses = computed(() => variantStyle.value.badge)
const buttonClasses = computed(() => variantStyle.value.button)

function handleBackdropClick() {
  if (props.dismissOnBackdrop) emit('close')
}

function handlePrimary() {
  emit('primary')
  emit('close')
}
</script>

<style scoped>
.backdrop-enter-active,
.backdrop-leave-active {
  transition: opacity 0.2s ease;
}
.backdrop-enter-from,
.backdrop-leave-to {
  opacity: 0;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
  transform: scale(0.96) translateY(4px);
}
</style>
